var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import { useChatMessage } from './hooks/utils';
function StatusIcon({ status }) {
    if (status === 'sending') {
        return (_jsx("svg", { viewBox: "0 0 16 16", width: 12, height: 12, "aria-hidden": "true", children: _jsx("circle", { cx: "8", cy: "8", r: "6", fill: "none", stroke: "currentColor", strokeWidth: "1.5", opacity: "0.4" }) }));
    }
    const seen = status === 'seen';
    return (_jsxs("svg", { viewBox: "0 0 18 12", width: 14, height: 10, "aria-hidden": "true", style: { color: seen ? '#0CC9A8' : '#94a3b8' }, children: [seen && (_jsx("path", { d: "M1 6.5 L4 9.5 L10 2", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" })), _jsx("path", { d: seen ? 'M6 6.5 L9 9.5 L17 2' : 'M3 6.5 L7 10 L15 2', fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" })] }));
}
function statusLabel(status) {
    switch (status) {
        case 'sending':
            return 'Sending';
        case 'delivered':
            return 'Delivered';
        case 'seen':
            return 'Seen';
    }
}
/**
 * Render a chat message body with support for the small set of markdown the
 * agent actually emits:
 *   - Markdown images:  ![alt](url)   → inline thumbnail
 *   - Markdown links:   [text](url)   → clickable link
 *   - Bold:             **text**      → <strong>
 *   - Bullet lists:     - item / * item → <ul>
 *   - Numbered lists:   1. item       → <ol>
 *   - Headings:         #..###### text → bold line
 *   - Plain text / line breaks everywhere else
 *
 * Why a bespoke parser instead of react-markdown? The agent only ever emits
 * these few markdown forms (per the support/retail prompts), so a small
 * tokenizer keeps the bundle slim AND avoids the XSS surface that comes with
 * rendering arbitrary markdown / raw HTML in a customer-facing widget.
 *
 * Two passes: a block pass groups lines into paragraphs / lists, and an inline
 * pass tokenizes bold, images and links within a line. The inline regex
 * matches bold first, then images (the leading `!`), then links — so an image
 * is never misread as a link.
 */
const INLINE_TOKEN = /\*\*([^*\n]+)\*\*|!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)/g;
function renderInline(text, keyBase) {
    var _a;
    const nodes = [];
    let lastIndex = 0;
    let key = 0;
    for (const match of text.matchAll(INLINE_TOKEN)) {
        const start = (_a = match.index) !== null && _a !== void 0 ? _a : 0;
        if (start > lastIndex) {
            nodes.push(text.slice(lastIndex, start));
        }
        const [, boldText, imgAlt, imgUrl, linkText, linkUrl] = match;
        if (boldText !== undefined) {
            nodes.push(_jsx("strong", { children: boldText }, `${keyBase}-b-${key++}`));
        }
        else {
            const url = imgUrl !== null && imgUrl !== void 0 ? imgUrl : linkUrl;
            // Reject anything that doesn't look like a real http(s) URL — guards
            // against javascript: / data: payloads sneaking through the LLM output.
            const safe = /^https?:\/\//i.test(url !== null && url !== void 0 ? url : '');
            if (imgUrl && safe) {
                nodes.push(
                // eslint-disable-next-line @next/next/no-img-element
                _jsx("img", { src: imgUrl, alt: imgAlt || 'Product image', loading: "lazy", className: "my-2 block max-w-[220px] rounded-md border border-white/20" }, `${keyBase}-img-${key++}`));
            }
            else if (linkUrl && safe) {
                nodes.push(_jsx("a", { href: linkUrl, target: "_blank", rel: "noopener noreferrer", className: "font-medium underline underline-offset-2 hover:opacity-80", children: linkText }, `${keyBase}-a-${key++}`));
            }
            else {
                // Unsafe URL — render the original text so nothing silently disappears
                nodes.push(match[0]);
            }
        }
        lastIndex = start + match[0].length;
    }
    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }
    return nodes;
}
const BULLET_RE = /^\s*[-*]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d+)\.\s+(.*)$/;
const HEADING_RE = /^\s*#{1,6}\s+(.*)$/;
function renderMessage(input) {
    // useChatMessage returns ReactNode when a custom messageFormatter is in
    // play (HTML, mentions, etc.); only run the markdown tokenizer on plain
    // strings — anything else is already rendered.
    if (typeof input !== 'string')
        return input;
    const text = input;
    if (!text)
        return text;
    const lines = text.split('\n');
    const blocks = [];
    // Buffers for the block currently being accumulated.
    let para = [];
    let list = null;
    let blockKey = 0;
    const flushPara = () => {
        if (!para.length)
            return;
        const joined = para.join('\n');
        // Drop whitespace-only paragraphs (blank lines between blocks) so they
        // don't render as empty spans that add stray vertical gaps.
        if (!joined.trim()) {
            para = [];
            return;
        }
        blocks.push(_jsx("span", { className: "whitespace-pre-wrap", children: renderInline(joined, `p${blockKey}`) }, `p-${blockKey++}`));
        para = [];
    };
    const flushList = () => {
        if (!list)
            return;
        const items = list.items;
        const Tag = list.ordered ? 'ol' : 'ul';
        blocks.push(_jsx(Tag, { className: cn('my-1 space-y-0.5 pl-5', list.ordered ? 'list-decimal' : 'list-disc'), children: items.map((it, i) => (_jsx("li", { className: "pl-0.5", children: renderInline(it, `li${blockKey}-${i}`) }, i))) }, `l-${blockKey++}`));
        list = null;
    };
    for (const line of lines) {
        const bullet = line.match(BULLET_RE);
        const ordered = !bullet ? line.match(ORDERED_RE) : null;
        const heading = !bullet && !ordered ? line.match(HEADING_RE) : null;
        if (bullet) {
            flushPara();
            if (!list || list.ordered) {
                flushList();
                list = { ordered: false, items: [] };
            }
            list.items.push(bullet[1]);
        }
        else if (ordered) {
            flushPara();
            if (!list || !list.ordered) {
                flushList();
                list = { ordered: true, items: [] };
            }
            list.items.push(ordered[2]);
        }
        else if (heading) {
            flushPara();
            flushList();
            blocks.push(_jsx("span", { className: "block font-semibold", children: renderInline(heading[1], `h${blockKey}`) }, `h-${blockKey++}`));
        }
        else {
            flushList();
            para.push(line);
        }
    }
    flushPara();
    flushList();
    return _jsx("span", { className: "flex flex-col gap-1", children: blocks });
}
export const ChatEntry = (_a) => {
    var _b, _c;
    var { entry, messageFormatter, hideName, hideTimestamp, status, className, ref } = _a, props = __rest(_a, ["entry", "messageFormatter", "hideName", "hideTimestamp", "status", "className", "ref"]);
    const { message, hasBeenEdited, time, locale, name } = useChatMessage(entry, messageFormatter);
    const isUser = (_c = (_b = entry.from) === null || _b === void 0 ? void 0 : _b.isLocal) !== null && _c !== void 0 ? _c : false;
    const messageOrigin = isUser ? 'remote' : 'local';
    const timeLabel = time.toLocaleTimeString(locale, { timeStyle: 'short' });
    return (_jsxs("li", Object.assign({ ref: ref, "data-lk-message-origin": messageOrigin, title: time.toLocaleTimeString(locale, { timeStyle: 'full' }), className: cn('group flex flex-col gap-0.5', className) }, props, { children: [!hideName && name && (_jsx("span", { className: "text-muted-foreground flex text-sm", children: _jsx("strong", { className: "mt-2", children: name }) })), _jsx("span", { className: cn('max-w-[75%] rounded-lg px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap', isUser
                    ? 'ml-auto border border-gray-200 bg-white text-gray-800'
                    : 'mr-auto bg-[#7C3AED] text-white'), children: isUser ? message : renderMessage(message) }), !hideTimestamp && (_jsxs("span", { className: cn('mt-0.5 flex items-center gap-1 text-[10.5px] text-gray-400', isUser ? 'ml-auto' : 'mr-auto'), children: [hasBeenEdited && _jsx("span", { className: "italic", children: "edited" }), _jsx("span", { className: "font-mono tabular-nums", children: timeLabel }), isUser && status ? (_jsx("span", { className: "flex items-center", "aria-label": `Message ${statusLabel(status).toLowerCase()}`, children: _jsx(StatusIcon, { status: status }) })) : null] }))] })));
};
